import * as React from 'react';

import type { EntWebsiteNativeDataSourceFragment } from 'bundles/ent-website/__generated__/contentfulTypes';
import { NativeDataContext } from 'bundles/ent-website/components/NativeDataContext';
import type { NativeDataKey } from 'bundles/ent-website/components/NativeDataContext';
import PromoDataSource from 'bundles/ent-website/contentful/modules/PromoDataSource';
import TeamsUnitPriceDataSource from 'bundles/ent-website/contentful/modules/TeamsUnitPriceDataSource';

type Props = {
  dataSources: Array<EntWebsiteNativeDataSourceFragment>;
};

/**
 * Auxiliary component to set the expected data sources in the context.
 * NOTE that this component should not be merged into `DataSourceLoader`.
 * The reason is that `DataSourceLoader` is a component that renders the data sources.
 * So the dependencies graph is like (-> is the direction of updating):
 *     DataSourceLoader -> *DataSource -> NativeDataContext -> ExpectedDataSources
 *
 * If we merge this component into `DataSourceLoader`,
 * the dependencies graph will become:
 *     DataSourceLoader -> *DataSource -> NativeDataContext -> DataSourceLoader
 * and cause an infinite re-render loop.
 */
function ExpectedDataSources({ dataSources }: Props) {
  const { setExpectedDataKeysRef } = React.useContext(NativeDataContext);
  const keys = dataSources.map((ds) => ds?.type).filter(Boolean) as NativeDataKey[];
  setExpectedDataKeysRef(keys);

  return null;
}

function DataSourceLoader({ dataSources }: Props) {
  return (
    <>
      <ExpectedDataSources dataSources={dataSources} />
      {dataSources.map((ds) => {
        let el;

        switch (ds.type) {
          case 'promo':
            el = <PromoDataSource key={ds.slug} />;
            break;
          case 'teamsUnitPrice':
            el = <TeamsUnitPriceDataSource key={ds.slug} />;
            break;
          default:
            el = null;
        }

        return el;
      })}
    </>
  );
}

export default DataSourceLoader;
